/** Canonical product-assignment helpers.
 * Ordered products are the only financial source of truth. Legacy transaction
 * columns are converted at the boundary so old orders remain readable.
 */
const getOrderedProducts = (transaction, config = {}) => {
  const canonical = transaction?.extras?.ordered_products;
  if (Array.isArray(canonical)) {
    return canonical.filter(Boolean).map((product) =>
      normalizeOrderedProduct(product, config.productsById?.[product.product_id]),
    );
  }

  const products = [];
  Object.entries(transaction?.bought_for || {}).forEach(
    (entry) => {
      const personId = entry[0];
      const quantity = entry[1];
      if (Number(quantity) <= 0) return;
      products.push({
        product_id: "legacy-beer",
        name: config.legacyProduct?.name || "Bier",
        quantity: Number(quantity),
        assigned_person_ids: [personId],
        price_cents: Math.round((config.BEER_PRICE || 0) * 100),
        deposit_cents: 0,
        splittable: false,
        metadata: { legacy: true },
      });
    },
  );
  Object.entries(transaction?.returned_cups || {}).forEach(
    (entry) => {
      const personId = entry[0];
      const quantity = entry[1];
      if (Number(quantity) <= 0) return;
      products.push({
        product_id: "legacy-cup-return",
        name: "Bekers retour",
        quantity: Number(quantity),
        assigned_person_ids: [personId],
        price_cents: 0,
        deposit_cents: -Math.round((config.CUP_REFUND || 0) * 100),
        splittable: false,
        metadata: { legacy: true, return_product: true },
      });
    },
  );
  (transaction?.extras?.products || []).forEach((product) => {
    const assignedPersonIds =
      product.assigned_person_ids || product.recipients || [];
    products.push(normalizeOrderedProduct({
      product_id: product.product_id,
      name:
        config.productsById?.[product.product_id]?.name ||
        product.name ||
        "Product",
      quantity: normalizedProductQuantity(Object.assign({}, product, {
        assigned_person_ids: assignedPersonIds,
      })),
      assigned_person_ids: assignedPersonIds,
      price_cents: Number(product.price_cents) || 0,
      deposit_cents: Number(product.deposit_cents) || 0,
      splittable: product.splittable ?? !!product.split,
      metadata: product.metadata || { legacy: true },
    }, config.productsById?.[product.product_id]));
  });
  return products;
};

/**
 * Older custom products did not persist a quantity. For a normal product every
 * selected recipient represents one item; a split product represents one item
 * shared by all recipients. This also repairs early schema-v2 lines that stored
 * zero instead of the derived quantity.
 */
const normalizedProductQuantity = (product) => {
  const quantity = Number(product?.quantity);
  if (quantity > 0) return quantity;
  const recipientCount = (product?.assigned_person_ids || product?.recipients || [])
    .filter(Boolean).length;
  return product?.splittable || product?.split ? (recipientCount ? 1 : 0) : recipientCount;
};

/**
 * Repair order snapshots made before a catalogue product was marked as a
 * return. The ordered amount remains an immutable price snapshot, but its sign
 * follows the product type so existing orders are included correctly in
 * balances as well as newly created orders.
 */
const normalizeOrderedProduct = (product, catalogueProduct) => {
  const name = catalogueProduct?.name || product?.name;
  const legacyCupReturn =
    String(name || '').trim().toLocaleLowerCase('nl-NL') === 'lege bekers';
  const returnProduct =
    catalogueProduct?.return_product === true ||
    product?.metadata?.return_product === true ||
    legacyCupReturn;
  const normalized = Object.assign({}, product, {
    // The catalogue name is current configuration. Financial values otherwise
    // remain the snapshots that were recorded when the order was created.
    name,
    quantity: normalizedProductQuantity(product),
  });

  if (!returnProduct) return normalized;

  const refundCents = Math.abs(
    Number(product?.deposit_cents) ||
      Number(product?.price_cents) ||
      Number(catalogueProduct?.deposit_cents) ||
      Number(catalogueProduct?.price_cents) ||
      0,
  );
  return Object.assign({}, normalized, {
    price_cents: 0,
    deposit_cents: -refundCents,
    splittable: false,
    metadata: Object.assign({}, product?.metadata, { return_product: true }),
  });
};

const productTotalCents = (product) =>
  (Number(product?.quantity) || 0) *
  ((Number(product?.price_cents) || 0) + (Number(product?.deposit_cents) || 0));


/** Derives every personal balance and detail from ordered product assignments. */
function calcSummary(transactions, cfg) {
  const config = {
    BEER_PRICE: cfg?.BEER_PRICE ?? 8,
    CUP_REFUND: cfg?.CUP_REFUND ?? 2,
  };
  const summary = {};
  const ensure = (userId) =>
    summary[userId] ||
    (summary[userId] = { userId, saldo: 0, tegen: {}, details: [] });

  (transactions || []).forEach((transaction) => {
    const buyerId = transaction.buyer_id;
    getOrderedProducts(transaction, Object.assign({}, config, {
      productsById: cfg?.productsById,
      legacyProduct:
        cfg?.legacyProductByMatchId?.[transaction.match_id] ||
        cfg?.legacyProduct,
    })).forEach((product) => {
      const recipients = Array.from(new Set(product.assigned_person_ids || []));
      if (!recipients.length) return;
      const total = productTotalCents(product) / 100;
      const share = product.splittable ? total / recipients.length : total;
      recipients.forEach((recipientId) => {
        const recipient = ensure(recipientId);
        const buyer = ensure(buyerId);
        if (recipientId === buyerId) {
          recipient.details.push({
            type: "product",
            target: buyerId,
            count: Number(product.quantity) || 0,
            amount: 0,
            productName: product.name || "Product",
            split: !!product.splittable,
            price: (Number(product.price_cents) || 0) / 100,
            deposit: (Number(product.deposit_cents) || 0) / 100,
            ownPurchase: true,
          });
          return;
        }
        recipient.saldo -= share;
        recipient.tegen[buyerId] = (recipient.tegen[buyerId] || 0) - share;
        recipient.details.push({
          type: "product",
          target: buyerId,
          count: Number(product.quantity) || 0,
          amount: -share,
          productName: product.name || "Product",
          split: !!product.splittable,
          price: (Number(product.price_cents) || 0) / 100,
          deposit: (Number(product.deposit_cents) || 0) / 100,
        });
        buyer.saldo += share;
        buyer.tegen[recipientId] = (buyer.tegen[recipientId] || 0) + share;
        buyer.details.push({
          type: "product",
          target: recipientId,
          count: Number(product.quantity) || 0,
          amount: share,
          productName: product.name || "Product",
          split: !!product.splittable,
          price: (Number(product.price_cents) || 0) / 100,
          deposit: (Number(product.deposit_cents) || 0) / 100,
        });
      });
    });
  });
  return summary;
}


// Keep this module CommonJS for both the React bundle and the Node push worker.
// Avoid spread/destructuring: browser transpilation can inject ESM helper imports
// into this file, causing webpack to lose these CommonJS exports.
module.exports = { getOrderedProducts, normalizedProductQuantity, normalizeOrderedProduct, productTotalCents, calcSummary };
